function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_without_holes(arr) {
    if (Array.isArray(arr)) return _array_like_to_array(arr);
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _non_iterable_spread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _to_consumable_array(arr) {
    return _array_without_holes(arr) || _iterable_to_array(arr) || _unsupported_iterable_to_array(arr) || _non_iterable_spread();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import { jsx as _jsx } from "react/jsx-runtime";
import { Button, Checkbox, FilePicker, Input, Radio, Select, Textarea } from "@neru/ui";
import * as R from "ramda";
import React, { useCallback, useContext, useEffect, useRef } from "react";
import { DatePicker } from "../DatePicker/DatePicker";
import { Dropdown } from "../Dropdown/Dropdown";
import { AppliedFilters } from "../Filter/AppliedFilters";
import { MagicFilterButton } from "../Filter/MagicFilterButton";
import { SearchInput } from "../Filter/SearchInput";
import { FormValueSetter } from "./FormValueSetter";
import { Validators } from "../../utils";
import { FormStateContext } from "./FormStateWrapper";
import { CONTROL_ACTIONS, SETTER_ACTIONS } from "./types";
/**
 * Form wrapper component. Traverses elements and their nested children, adding
 * onInput, onChange and onClick handlers to all known elements (depends on type).
 *
 * All elements are controlled (https://reactjs.org/docs/uncontrolled-components.html)
 * by parent Form component, thus setting default values should be done via Form's
 * initialValues property.
 *
 * All children should be accessible from the root Form component either directly
 * or through children prop in order to become controlled by it.
 *
 * Button of type "submit" submits Form values, triggering onFinish callback.
 * Button of type "reset" clears all values and triggers onFinish callback.
 * Button of type "button" is ignored by Form, meaning no special handlers are
 * bound to it.
 *
 * FormValueSetter is a special component, which allows to control form values
 * directly, updating its state with specified value. Common usage is to clear
 * specifically selected property (different from clearing all values with Reset).
 *
 */ export var FormWrapper = function(param) {
    var children = param.children, _param_initialRequiredFields = param.initialRequiredFields, initialRequiredFields = _param_initialRequiredFields === void 0 ? [] : _param_initialRequiredFields, _param_initialValues = param.initialValues, initialValues = _param_initialValues === void 0 ? {} : _param_initialValues, name = param.name, onFinish = param.onFinish, _param_root = param.root, root = _param_root === void 0 ? false : _param_root;
    var requiredFields = useRef(initialRequiredFields);
    var formSubmitButton = useRef();
    var isMultiRef = useRef({});
    var _useContext = useContext(FormStateContext), dispatch = _useContext.dispatch, state = _useContext.state;
    useEffect(function() {
        if (state.command) {
            onFinish(state.form);
            dispatch({
                payload: null,
                type: CONTROL_ACTIONS.command
            });
        }
    }, [
        state.command
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(function() {
        var button = formSubmitButton.current;
        if (isEmptyRequiredField() && button && !button.disabled) {
            dispatch({
                payload: null,
                type: CONTROL_ACTIONS.command
            });
        }
    });
    var onChange = R.curry(function(fieldName, value) {
        dispatch({
            payload: {
                fieldName: fieldName,
                value: value
            },
            type: SETTER_ACTIONS.set
        });
    });
    var WithFormWrapper = useCallback(function(param) {
        var children = param.children;
        return /*#__PURE__*/ _jsx(FormWrapper, {
            initialRequiredFields: requiredFields.current,
            initialValues: initialValues,
            name: name,
            onFinish: onFinish,
            children: children
        });
    }, []);
    // Disabled unless all required fields have value
    var isEmptyRequiredField = function() {
        var _requiredFields_current;
        return ((_requiredFields_current = requiredFields.current) !== null && _requiredFields_current !== void 0 ? _requiredFields_current : []).some(function(fieldName) {
            return R.isEmpty(state.form[fieldName]) || R.isNil(state.form[fieldName]);
        });
    };
    var isMultiChoice = useCallback(function(name, value) {
        var _isMultiRef_current_name, _isMultiRef_current_name1;
        isMultiRef.current[name] = ((_isMultiRef_current_name = isMultiRef.current[name]) !== null && _isMultiRef_current_name !== void 0 ? _isMultiRef_current_name : []).includes(value) ? isMultiRef.current[name] : _to_consumable_array((_isMultiRef_current_name1 = isMultiRef.current[name]) !== null && _isMultiRef_current_name1 !== void 0 ? _isMultiRef_current_name1 : []).concat([
            value
        ]);
    }, []);
    var setCheckboxValue = R.curry(function(fieldName, checked, value) {
        if (checked) {
            dispatch({
                payload: {
                    fieldName: fieldName,
                    value: isMultiRef.current[fieldName.toString()].length === 1 ? value : [
                        value
                    ]
                },
                type: SETTER_ACTIONS.insert
            });
        } else {
            dispatch({
                payload: {
                    fieldName: fieldName,
                    value: value
                },
                type: SETTER_ACTIONS.remove
            });
        }
    });
    var isChecked = function(fieldName, value) {
        var optionValues = state.form[fieldName];
        return Array.isArray(optionValues) ? optionValues.includes(value) : String(optionValues) === String(value);
    };
    var addTestId = useCallback(function(child) {
        var _child_props_datatestid;
        return {
            "data-testid": (_child_props_datatestid = child.props["data-testid"]) !== null && _child_props_datatestid !== void 0 ? _child_props_datatestid : "".concat(name, "-").concat(child.props.name)
        };
    }, [
        name
    ]);
    var setFormValue = useCallback(function(shouldSubmit) {
        return function(type, fieldName, value) {
            dispatch({
                payload: {
                    fieldName: fieldName,
                    value: type === SETTER_ACTIONS.set ? value : [
                        value
                    ]
                },
                type: type
            });
            if (shouldSubmit) {
                dispatch({
                    payload: "submit",
                    type: CONTROL_ACTIONS.command
                });
            }
        };
    }, [
        dispatch
    ]);
    var handleReset = function(onClickHandler) {
        return function(e) {
            e.preventDefault();
            // If button itself has an event listener
            if (onClickHandler) {
                onClickHandler(e);
            }
            dispatch({
                payload: initialValues,
                type: CONTROL_ACTIONS.reset
            });
            onFinish(initialValues);
        };
    };
    var onSubmit = function(e) {
        e.preventDefault();
        var _requiredFields_current;
        if (Validators.hasAllRequiredFields(state.form, (_requiredFields_current = requiredFields.current) !== null && _requiredFields_current !== void 0 ? _requiredFields_current : [])) {
            onFinish(state.form);
        }
    };
    var initTextInput = function(child) {
        var handleKeydown = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : false;
        var _child_props = child.props, name = _child_props.name, onInput = _child_props.onInput, type = _child_props.type, value = _child_props.value;
        var _ref;
        return /*#__PURE__*/ React.cloneElement(child, _object_spread(_object_spread_props(_object_spread({
            value: (_ref = value !== null && value !== void 0 ? value : state.form[name]) !== null && _ref !== void 0 ? _ref : ""
        }, addTestId(child)), {
            onInput: function(e) {
                onInput === null || onInput === void 0 ? void 0 : onInput(e);
                onChange(name, e.currentTarget.value);
            }
        }), handleKeydown ? {
            onKeyDown: function(e) {
                if (e.key === "Enter") {
                    if (type === "search") {
                        onSubmit(e);
                    } else {
                        e.preventDefault();
                    }
                }
            }
        } : null));
    };
    var initInputElementsWithName = function(child) {
        var _child_props = child.props, name = _child_props.name, type = _child_props.type, value = _child_props.value;
        var _child_props_required;
        var required = (_child_props_required = child.props.required) !== null && _child_props_required !== void 0 ? _child_props_required : child.props["required"];
        var _requiredFields_current;
        var requiredFieldsRef = (_requiredFields_current = requiredFields.current) !== null && _requiredFields_current !== void 0 ? _requiredFields_current : [];
        if (required && !requiredFieldsRef.includes(name)) {
            requiredFields.current.push(name);
        }
        switch(child.type){
            case "input":
                if (type === "file") {
                    var onAddFiles = onChange(name);
                    return /*#__PURE__*/ React.cloneElement(child, _object_spread_props(_object_spread({}, addTestId(child)), {
                        onChange: function(e) {
                            var _child_props_onChange, _child_props;
                            (_child_props_onChange = (_child_props = child.props).onChange) === null || _child_props_onChange === void 0 ? void 0 : _child_props_onChange.call(_child_props, e);
                            onAddFiles(e.currentTarget.files);
                        }
                    }));
                } else if (type === "hidden") {
                    return /*#__PURE__*/ React.cloneElement(child, _object_spread_props(_object_spread({}, addTestId(child)), {
                        value: value !== null && value !== void 0 ? value : state.form[name]
                    }));
                }
                break;
            case Input:
                return initTextInput(child, true);
            case Textarea:
                return initTextInput(child);
            case Select:
                {
                    var onSelect = onChange(name);
                    var _child_props_defaultValue;
                    return /*#__PURE__*/ React.cloneElement(child, _object_spread_props(_object_spread({}, addTestId(child)), {
                        defaultValue: (_child_props_defaultValue = child.props.defaultValue) !== null && _child_props_defaultValue !== void 0 ? _child_props_defaultValue : state.form[child.props.name],
                        onChange: function(e) {
                            var _child_props_onChange, _child_props;
                            (_child_props_onChange = (_child_props = child.props).onChange) === null || _child_props_onChange === void 0 ? void 0 : _child_props_onChange.call(_child_props, e);
                            onSelect(e);
                        }
                    }));
                }
            case Checkbox:
                {
                    var onCheck = setCheckboxValue(name);
                    isMultiChoice(name, value);
                    return /*#__PURE__*/ React.cloneElement(child, _object_spread_props(_object_spread({}, child.props, addTestId(child)), {
                        checked: R.isNil(child.props.checked) ? isChecked(name, value) : child.props.checked,
                        onClick: function(e) {
                            onCheck(e.currentTarget.checked, e.currentTarget.value);
                        }
                    }));
                }
            case Radio:
                {
                    var onClick = onChange(name);
                    return /*#__PURE__*/ React.cloneElement(child, _object_spread_props(_object_spread({}, child.props, addTestId(child)), {
                        checked: R.isNil(child.props.checked) ? isChecked(name, value) : child.props.checked,
                        onClick: function(e) {
                            return onClick(e.currentTarget.value);
                        }
                    }));
                }
            case DatePicker:
                return /*#__PURE__*/ React.cloneElement(child, _object_spread_props(_object_spread({}, addTestId(child)), {
                    onChange: onChange(name),
                    value: state.form[child.props.name]
                }));
            case FilePicker:
                return /*#__PURE__*/ React.cloneElement(child, _object_spread_props(_object_spread({}, addTestId(child)), {
                    onChange: function(files) {
                        var _child_props_onChange, _child_props;
                        (_child_props_onChange = (_child_props = child.props).onChange) === null || _child_props_onChange === void 0 ? void 0 : _child_props_onChange.call(_child_props, files);
                        onChange(name)(files);
                    },
                    value: state.form[child.props.name]
                }));
        }
    };
    var processButtonElement = function(child) {
        if (child.props.type === "reset") {
            return /*#__PURE__*/ React.cloneElement(child, {
                onClick: handleReset(child.props.onClick),
                role: "button"
            });
        } else if (child.props.type === "submit") {
            return /*#__PURE__*/ React.cloneElement(child, {
                disabled: child.props.disabled || isEmptyRequiredField(),
                ref: function(el) {
                    return formSubmitButton.current = el;
                },
                role: "button"
            });
        } else {
            return /*#__PURE__*/ React.cloneElement(child, {
                role: "button"
            });
        }
    };
    var processSearchInputElement = function(child) {
        return /*#__PURE__*/ React.cloneElement(child, {
            initFormElements: initFormElements,
            onClear: function(e) {
                var _child_props_onClear, _child_props;
                (_child_props_onClear = (_child_props = child.props).onClear) === null || _child_props_onClear === void 0 ? void 0 : _child_props_onClear.call(_child_props, e);
                dispatch({
                    payload: {
                        fieldName: child.props.name
                    },
                    type: SETTER_ACTIONS.set
                });
                dispatch({
                    payload: "submit",
                    type: CONTROL_ACTIONS.command
                });
            },
            value: state.form[child.props.name]
        });
    };
    var initFormElements = function(formChildren) {
        return React.Children.map(formChildren, function(child) {
            var _child_props, _child_props1, _child_props2;
            if (!child) return;
            if (!Validators.isJSXElement(child)) {
                return child;
            }
            switch(child.type){
                case FormValueSetter:
                    // Handle special FormValueSetter element
                    return /*#__PURE__*/ React.cloneElement(child, {
                        onClick: setFormValue(child.props.submit)
                    });
                case Dropdown:
                    var _child_props_defaultValue;
                    return /*#__PURE__*/ React.cloneElement(child, _object_spread_props(_object_spread({
                        selectedValues: (_child_props_defaultValue = child.props.defaultValue) !== null && _child_props_defaultValue !== void 0 ? _child_props_defaultValue : state.form[child.props.name]
                    }, addTestId(child)), {
                        initFormElements: initFormElements
                    }));
                case AppliedFilters:
                case MagicFilterButton:
                    // Inject form elements initializer function into special Filter elements
                    return /*#__PURE__*/ React.cloneElement(child, {
                        initFormElements: initFormElements
                    });
                case SearchInput:
                    return processSearchInputElement(child);
                case Button:
                    return processButtonElement(child);
            }
            if (((_child_props = child.props) === null || _child_props === void 0 ? void 0 : _child_props.withForm) === true) {
                return /*#__PURE__*/ React.cloneElement(child, {
                    FormWrapper: WithFormWrapper,
                    form: {
                        setValue: onChange,
                        state: state.form
                    }
                });
            }
            if ((_child_props1 = child.props) === null || _child_props1 === void 0 ? void 0 : _child_props1.name) {
                return initInputElementsWithName(child);
            }
            // Depth-first search for recognized form elements in the DOM tree
            if ((_child_props2 = child.props) === null || _child_props2 === void 0 ? void 0 : _child_props2.children) {
                return /*#__PURE__*/ React.cloneElement(child, {
                    children: initFormElements(child.props.children)
                });
            }
            return child;
        });
    };
    return root ? /*#__PURE__*/ _jsx("form", {
        "data-testid": "form-".concat(name),
        name: name,
        onSubmit: onSubmit,
        children: initFormElements(children)
    }) : initFormElements(children);
};

//# sourceMappingURL=FormWrapper.js.map